import { useState } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import { MagicIcon, OpenAIIcon } from "@excalidraw/excalidraw/components/icons";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { CheckboxItem } from "@excalidraw/excalidraw/components/CheckboxItem";
import { KEYS } from "@excalidraw/common/keys";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { InlineIcon } from "@excalidraw/excalidraw/components/InlineIcon";
import { Paragraph } from "@excalidraw/excalidraw/components/Paragraph";

import "./MagicSettings.scss";

export type MagicSettingsProps = {
  openAIKey: string | null;
  openAIBaseURL: string | null;
  openAIModelName: string | null;
  isPersisted: boolean;
  onChange: (settings: {
    key: string;
    baseURL: string;
    modelName: string;
    shouldPersist: boolean;
  }) => void;
  onConfirm: (settings: {
    key: string;
    baseURL: string;
    modelName: string;
    shouldPersist: boolean;
  }) => void;
  onClose: () => void;
};

export const MagicSettings = (props: MagicSettingsProps) => {
  const [keyInputValue, setKeyInputValue] = useState(props.openAIKey || "");
  const [baseURLInputValue, setBaseURLInputValue] = useState(
    props.openAIBaseURL || "",
  );
  const [modelNameInputValue, setModelNameInputValue] = useState(
    props.openAIModelName || "",
  );
  const [shouldPersist, setShouldPersist] = useState<boolean>(
    props.isPersisted,
  );

  const appState = useUIAppState();

  const getSettings = () => {
    return {
      key: keyInputValue.trim(),
      baseURL: baseURLInputValue.trim(),
      modelName: modelNameInputValue.trim(),
      shouldPersist,
    };
  };

  const onConfirm = () => {
    props.onConfirm(getSettings());
  };

  const handleChange = (
    updates: Partial<ReturnType<typeof getSettings>>,
    updateState = true,
  ) => {
    const newState = {
      key: "key" in updates ? updates.key! : keyInputValue,
      baseURL: "baseURL" in updates ? updates.baseURL! : baseURLInputValue,
      modelName:
        "modelName" in updates ? updates.modelName! : modelNameInputValue,
      shouldPersist:
        "shouldPersist" in updates ? updates.shouldPersist! : shouldPersist,
    };
    if (updateState) {
      setKeyInputValue(newState.key);
      setBaseURLInputValue(newState.baseURL);
      setModelNameInputValue(newState.modelName);
      setShouldPersist(newState.shouldPersist);
    }
    props.onChange({
      key: newState.key.trim(),
      baseURL: newState.baseURL.trim(),
      modelName: newState.modelName.trim(),
      shouldPersist: newState.shouldPersist,
    });
  };

  if (appState.openDialog?.name !== "settings") {
    return null;
  }

  return (
    <Dialog
      onCloseRequest={() => {
        props.onClose();
        props.onConfirm(getSettings());
      }}
      title={
        <div style={{ display: "flex" }}>
          线框图转代码（AI）{" "}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.1rem 0.5rem",
              marginLeft: "1rem",
              fontSize: 14,
              borderRadius: "12px",
              color: "#000",
              background: "pink",
            }}
          >
            实验性功能
          </div>
        </div>
      }
      className="MagicSettings"
      autofocus={false}
    >
      <Paragraph>
        图形转代码功能会调用 <InlineIcon icon={OpenAIIcon} /> OpenAI。
      </Paragraph>
      <Paragraph>
        由于 OpenAI API 仍有使用限制，这里需要配置你自己的 API key。你可以创建
        <a
          href="https://platform.openai.com/login?launch"
          rel="noopener noreferrer"
          target="_blank"
        >
          OpenAI 账号
        </a>
        ，充值少量余额，并
        <a
          href="https://platform.openai.com/api-keys"
          rel="noopener noreferrer"
          target="_blank"
        >
          生成自己的 API key
        </a>
        。
      </Paragraph>
      <Paragraph>
        OpenAI key 不会离开浏览器。如有需要，也可以在 OpenAI 控制台中设置用量上限。
      </Paragraph>
      <TextField
        isRedacted
        value={keyInputValue}
        placeholder="在这里粘贴你的 API key"
        label="OpenAI API key"
        onChange={(value) => handleChange({ key: value })}
        selectOnRender
        onKeyDown={(event) => event.key === KEYS.ENTER && onConfirm()}
      />
      <TextField
        value={baseURLInputValue}
        placeholder="例如 https://api.openai.com/v1"
        label="Base URL（可选）"
        onChange={(value) => handleChange({ baseURL: value })}
        onKeyDown={(event) => event.key === KEYS.ENTER && onConfirm()}
      />
      <TextField
        value={modelNameInputValue}
        placeholder="例如 gpt-4-vision-preview"
        label="模型名称（可选）"
        onChange={(value) => handleChange({ modelName: value })}
        onKeyDown={(event) => event.key === KEYS.ENTER && onConfirm()}
      />
      <Paragraph>
        默认不会持久保存 API token，刷新页面后需要重新填写。你也可以选择将它保存在当前浏览器中。
      </Paragraph>

      <CheckboxItem
        checked={shouldPersist}
        onChange={(checked) => handleChange({ shouldPersist: checked })}
      >
        将 API key 保存在浏览器存储中
      </CheckboxItem>

      <Paragraph>
        设置 API key 后，可以使用 <InlineIcon icon={MagicIcon} /> 工具把元素放入画框，
        再将其转换成代码。也可以通过 <b>AI 设置</b>{" "}
        <InlineIcon icon={OpenAIIcon} /> 打开此窗口。
      </Paragraph>

      <FilledButton
        className="MagicSettings__confirm"
        size="large"
        label="确认"
        onClick={onConfirm}
      />
    </Dialog>
  );
};
